import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import {
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdOutlineIndeterminateCheckBox,
} from "react-icons/md";
import { twMerge } from "tailwind-merge";
import Button from "../../../../../components/shared/Button/Button";
import { useId } from "../../../../../hooks/useId";
import Divider from "../../../../../components/shared/Divider/Divider";
import Checkbox from "../../../../../components/shared/Checkbox";

export function SelectClub({ header, onChange, options, placeholder, values }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const id = useId();

  const allFlat = useMemo(() => options.flatMap((c) => c.options), [options]);

  const firstSelectedText = useMemo(() => {
    const first = allFlat.find((o) => String(o.value) === String(values[0]));
    return first?.text ?? "";
  }, [allFlat, values]);

  const summary = useMemo(() => {
    if (!values?.length) return placeholder;
    const extra = values.length > 1 ? `+${values.length - 1}` : "";
    return `${firstSelectedText} ${extra}`.trim();
  }, [values, placeholder, firstSelectedText]);

  const getCategoryCheckboxState = (category) => {
    const ids = new Set(category.options.map((o) => String(o.value)));
    const selectedIn = values.filter((v) => ids.has(String(v))).length;
    if (selectedIn === 0) return "none";
    if (selectedIn === category.options.length) return "all";
    return "some";
  };

  const handleOptionClick = (val) => {
    if (values.some((v) => String(v) === String(val))) {
      const next = values.filter((v) => String(v) !== String(val));
      onChange(next.length ? next : []);
    } else {
      onChange([...values, val]);
    }
  };

  const handleCategoryToggle = (category) => {
    const catValues = category.options.map((o) => String(o.value));
    const selectedSet = new Set(values.map((v) => String(v)));
    const state = getCategoryCheckboxState(category);

    if (state === "all") {
      // Deselect all from this category, keep others intact
      const next = values.filter((v) => !catValues.includes(String(v)));
      onChange(next);
    } else {
      // Select all from this category (union), keep others intact
      const union = [
        ...values,
        ...category.options
          .map((o) => o.value)
          .filter((v) => !selectedSet.has(String(v))),
      ];
      onChange(union);
    }
  };

  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) close();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    active?.scrollIntoView({ block: "nearest" });
  }, [open, values]);

  return (
    <div className={twMerge("relative")} ref={rootRef}>
      <Button
        className="flex flex-row items-center gap-2"
        color={values?.length ? "primary50" : "gray100"}
        onClick={() => setOpen((o) => !o)}
        ref={buttonRef}
        rounded="full"
        size="sm"
      >
        {summary}
        <FiChevronDown
          className={twMerge(
            "shrink-0 transition-transform",
            open && "rotate-180"
          )}
        />
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="absolute z-20 mt-1 w-[200px]"
            exit={{ opacity: 0, scale: 0.98, y: -4 }}
            initial={{ opacity: 0, scale: 0.98, y: -4 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
          >
            <div className="border-gray200 max-h-[28rem] overflow-hidden rounded-md border bg-white shadow-lg">
              {header && (
                <>
                  <div className="px-4 py-3 text-base font-semibold text-black">
                    {header}
                  </div>
                  <Divider color="gray200" type="horizontal" />
                </>
              )}

              <div
                className="max-h-96 overflow-y-auto"
                id={`${id}-listbox`}
                ref={listRef}
                role="listbox"
                tabIndex={-1}
              >
                {options.map((category) => {
                  const state = getCategoryCheckboxState(category);
                  return (
                    <div key={category.label}>
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="text-gray800 text-sm font-semibold">
                          {category.label}
                        </div>

                        <Checkbox
                          isChecked={state === "some" || state === "all"}
                          isSome={state === "some"}
                          onChange={() => handleCategoryToggle(category)}
                        />
                      </div>

                      <Divider color="gray200" type="horizontal" />

                      <div className="flex flex-col gap-1 px-1.5 py-1">
                        {category.options.map((opt) => {
                          const isChecked = values.some(
                            (v) => String(v) === String(opt.value)
                          );
                          return (
                            <button
                              aria-selected={isChecked}
                              className={twMerge(
                                "flex w-full cursor-pointer items-center gap-2 rounded-sm p-2.5 text-left text-sm",
                                "hover:bg-gray100 focus:bg-gray100 focus:outline-none"
                              )}
                              data-active={isChecked ? "true" : undefined}
                              key={String(opt.value)}
                              onClick={() => handleOptionClick(opt.value)}
                              role="option"
                              type="button"
                            >
                              <Checkbox isChecked={isChecked} />

                              <span className="truncate">{opt.text}</span>
                            </button>
                          );
                        })}
                      </div>

                      {category !== options[options.length - 1] && (
                        <Divider color="gray200" type="horizontal" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
